/**
 * SellerContingencyHub.js
 * Seller-facing dashboard for responding to inspection issues and appraisal gaps.
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  useSellerContingencies,
  useInspectionResponse,
  useAppraisalResponse,
} from '../../agents/contingencyAgent';
import './SellerContingencyHub.css';
import ContractHub from '../ContingencyHub/contract/ContractHub';
import { contractCopy } from '../ContingencyHub/contract/contractCopy';

/**
 * WI: the inspection/appraisal responses below are proposals, not written
 * notices (design §3.5), so a Wisconsin card carries this banner.
 */
function ProposalsBanner({ transaction }) {
  if (!transaction.is_contract_tracked) return null;
  return (
    <div className="sch-proposals" role="note" data-testid={`seller-proposals-banner-${transaction.id}`}>
      <p className="sch-proposals__heading">{contractCopy('WI.hub.proposals.heading')}</p>
      <p className="sch-proposals__text">{contractCopy('WI.hub.proposals.banner')}</p>
    </div>
  );
}

function contractTransactionTitle(tx) {
  const locality = [tx.property_city, tx.property_state].filter(Boolean).join(', ');
  return [tx.property_address, locality].filter(Boolean).join(', ') || `Transaction ${tx.id}`;
}

const INSPECTION_DECISION_HELP = {
  accept: 'Use when you agree to complete the repair as requested.',
  credit: 'Use when you prefer a financial credit instead of completing work before closing.',
  reject: 'Use when the request is not acceptable as submitted.',
};

const APPRAISAL_DECISION_HELP = {
  reduce_price: 'Most buyer-friendly option. Helps keep financing on track.',
  split_difference: 'Balanced compromise that shares the appraisal gap.',
  reject_gap: 'Use when you are not willing to adjust terms for the gap.',
};

function CompactTimelineRail({ steps, currentIndex }) {
  if (!steps?.length) return null;
  return (
    <div className="sch-timeline" role="list" aria-label="Response progress timeline">
      {steps.map((step, idx) => {
        const isDone = idx < currentIndex;
        const isCurrent = idx === currentIndex;
        return (
          <div className="sch-timeline__item" role="listitem" key={step.label}>
            <span className={`sch-timeline__dot${isDone ? ' is-done' : ''}${isCurrent ? ' is-current' : ''}`} aria-hidden="true">
              {isDone ? '✓' : idx + 1}
            </span>
            <span className={`sch-timeline__label${isCurrent ? ' is-current' : ''}`}>
              {step.icon ? <span className="sch-timeline__label-icon" aria-hidden="true">{step.icon}</span> : null}
              {step.label}
            </span>
            {idx < steps.length - 1 && <span className="sch-timeline__line" aria-hidden="true" />}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// InspectionTransactionCard — one property's inspection issues
// ---------------------------------------------------------------------------
function InspectionTransactionCard({ transaction, onSubmitted }) {
  const { submitInspectionResponse, loading, error } = useInspectionResponse(transaction.id);
  const issues = useMemo(() => transaction.inspection_issues || [], [transaction.inspection_issues]);

  // Local state: { [issue_id]: { response: 'accept'|'credit'|'reject', credit_amount: '' } }
  const [decisions, setDecisions] = useState(() =>
    Object.fromEntries(issues.map((issue) => [issue.id, { response: '', credit_amount: '' }]))
  );
  const [submitError, setSubmitError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const answeredCount = useMemo(
    () => issues.filter((i) => Boolean(decisions[i.id]?.response)).length,
    [issues, decisions]
  );

  const needsCreditAmount = useMemo(
    () => issues.some((i) => decisions[i.id]?.response === 'credit' && !(parseFloat(decisions[i.id]?.credit_amount) > 0)),
    [issues, decisions]
  );

  const allAnswered = issues.length > 0 && answeredCount === issues.length;
  const inspectionTimelineIndex = submitted ? 2 : answeredCount > 0 ? 1 : 0;

  const setDecision = useCallback((issueId, field, value) => {
    setDecisions((prev) => ({
      ...prev,
      [issueId]: { ...prev[issueId], [field]: value },
    }));
  }, []);

  const handleSubmit = useCallback(async () => {
    setSubmitError(null);
    const unanswered = issues.filter((i) => !decisions[i.id]?.response);
    if (unanswered.length > 0) {
      setSubmitError('Please select a response for every issue before submitting.');
      return;
    }

    if (needsCreditAmount) {
      setSubmitError('Please enter a valid credit amount for each issue marked as Request Credit.');
      return;
    }

    const responses = issues.map((issue) => {
      const d = decisions[issue.id];
      const entry = { issue_id: issue.id, seller_response: d.response };
      if (d.response === 'credit' && d.credit_amount) {
        entry.credit_amount = parseFloat(d.credit_amount);
      }
      return entry;
    });

    const result = await submitInspectionResponse(responses);
    if (result.success) {
      setSubmitted(true);
      if (onSubmitted) onSubmitted(transaction.id);
    } else {
      setSubmitError(result.error || 'Submission failed. Please try again.');
    }
  }, [decisions, issues, needsCreditAmount, submitInspectionResponse, onSubmitted, transaction.id]);

  if (submitted) {
    return (
      <div className="sch-card sch-card--done">
        <p className="sch-card__done-msg">Responses submitted for {transaction.property_address}.</p>
      </div>
    );
  }

  return (
    <div className="sch-card">
      <div className="sch-card__header">
        <h3 className="sch-card__address">{transaction.property_address || `Transaction ${transaction.id}`}</h3>
        <span className="sch-badge sch-badge--inspection">Inspection</span>
      </div>
      <ProposalsBanner transaction={transaction} />

      <CompactTimelineRail
        steps={[
          { label: 'Received', icon: '📥' },
          { label: 'Review & Decide', icon: '🧠' },
          { label: 'Response Sent', icon: '📤' },
        ]}
        currentIndex={inspectionTimelineIndex}
      />

      <div className="sch-card__guide">
        <div className="sch-card__guide-top">
          <span className="sch-card__guide-title">Step 1: Respond to each issue</span>
          <span className="sch-card__guide-progress">{answeredCount}/{issues.length} completed</span>
        </div>
        <div className="sch-card__progress-track" aria-hidden="true">
          <div className="sch-card__progress-fill" style={{ width: `${issues.length ? (answeredCount / issues.length) * 100 : 0}%` }} />
        </div>
        <p className="sch-helper-text">Step 2: Submit all responses together to send one complete package to the buyer.</p>
      </div>

      {issues.length === 0 ? (
        <p className="sch-empty">No inspection issues on record.</p>
      ) : (
        <div className="sch-issues">
          {issues.map((issue) => (
            <div key={issue.id} className="sch-issue">
              <div className="sch-issue__top">
                <span className="sch-issue__desc">{issue.description || issue.issue_description}</span>
                <span className={`sch-severity sch-severity--${issue.severity}`}>
                  {issue.severity}
                </span>
              </div>
              <div className="sch-issue__controls">
                <div className="sch-btn-group">
                  {['accept', 'credit', 'reject'].map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      className={`sch-choice-btn ${decisions[issue.id]?.response === opt ? 'sch-choice-btn--active' : ''}`}
                      onClick={() => setDecision(issue.id, 'response', opt)}
                    >
                      {opt === 'accept' ? 'Accept' : opt === 'credit' ? 'Request Credit' : 'Reject'}
                    </button>
                  ))}
                </div>
                {decisions[issue.id]?.response && (
                  <p className="sch-helper-text" style={{ margin: 0 }}>
                    {INSPECTION_DECISION_HELP[decisions[issue.id]?.response]}
                  </p>
                )}
                {decisions[issue.id]?.response === 'credit' && (
                  <div className="sch-credit-input">
                    <label className="sch-label" htmlFor={`credit-${issue.id}`}>Credit Amount ($)</label>
                    <input
                      id={`credit-${issue.id}`}
                      type="number"
                      min="0"
                      step="1"
                      className="sch-input"
                      placeholder="e.g. 5000"
                      value={decisions[issue.id]?.credit_amount || ''}
                      onChange={(e) => setDecision(issue.id, 'credit_amount', e.target.value)}
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {(submitError || error) && (
        <p className="sch-error">{submitError || error}</p>
      )}

      {issues.length > 0 && (
        <div className="sch-card__footer">
          <button
            type="button"
            className="sch-submit-btn"
            disabled={loading || !allAnswered || needsCreditAmount}
            onClick={handleSubmit}
          >
            {loading ? 'Submitting...' : `Submit Responses (${answeredCount}/${issues.length})`}
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AppraisalTransactionCard — one property's appraisal gap decision
// ---------------------------------------------------------------------------
function AppraisalTransactionCard({ transaction, onSubmitted }) {
  const { submitAppraisalResponse, loading, error } = useAppraisalResponse(transaction.id);
  const [decision, setDecision] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [submitError, setSubmitError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const appraisalData = transaction.appraisal_data || {};
  const appraisedValue = appraisalData.appraised_value;
  const offerPrice = appraisalData.offer_price ?? transaction.offer_price;
  const gap = offerPrice && appraisedValue ? offerPrice - appraisedValue : null;
  const gapPercent = offerPrice && appraisedValue ? ((offerPrice - appraisedValue) / offerPrice) * 100 : null;
  const appraisalTimelineIndex = submitted ? 2 : decision ? 1 : 0;

  const recommendedDecision = useMemo(() => {
    if (gapPercent == null || gapPercent <= 0) return null;
    if (gapPercent <= 2) return 'split_difference';
    if (gapPercent <= 5) return 'reduce_price';
    return 'reduce_price';
  }, [gapPercent]);

  const handleSubmit = useCallback(async () => {
    setSubmitError(null);
    if (!decision) {
      setSubmitError('Please select a decision before submitting.');
      return;
    }
    const priceReduction = decision === 'reduce_price' && newPrice ? parseFloat(newPrice) : undefined;
    const result = await submitAppraisalResponse(decision, priceReduction, {
      offerPrice: offerPrice ?? undefined,
      gap: gap ?? undefined,
      appraisedValue: appraisedValue ?? undefined,
    });
    if (result.success) {
      setSubmitted(true);
      if (onSubmitted) onSubmitted(transaction.id);
    } else {
      setSubmitError(result.error || 'Submission failed. Please try again.');
    }
  }, [decision, newPrice, submitAppraisalResponse, onSubmitted, transaction.id, appraisedValue, gap, offerPrice]);

  if (submitted) {
    return (
      <div className="sch-card sch-card--done">
        <p className="sch-card__done-msg">Appraisal decision submitted for {transaction.property_address}.</p>
      </div>
    );
  }

  return (
    <div className="sch-card">
      <div className="sch-card__header">
        <h3 className="sch-card__address">{transaction.property_address || `Transaction ${transaction.id}`}</h3>
        <span className="sch-badge sch-badge--appraisal">Appraisal</span>
      </div>
      <ProposalsBanner transaction={transaction} />

      <CompactTimelineRail
        steps={[
          { label: 'Received', icon: '📥' },
          { label: 'Review & Decide', icon: '🧠' },
          { label: 'Response Sent', icon: '📤' },
        ]}
        currentIndex={appraisalTimelineIndex}
      />

      <div className="sch-appraisal-summary">
        {appraisedValue != null && (
          <div className="sch-appraisal-row">
            <span className="sch-appraisal-label">Appraised Value</span>
            <span className="sch-appraisal-value">${appraisedValue.toLocaleString()}</span>
          </div>
        )}
        {offerPrice != null && (
          <div className="sch-appraisal-row">
            <span className="sch-appraisal-label">Offer Price</span>
            <span className="sch-appraisal-value">${offerPrice.toLocaleString()}</span>
          </div>
        )}
        {gap != null && (
          <div className="sch-appraisal-row sch-appraisal-row--gap">
            <span className="sch-appraisal-label">Appraisal Gap</span>
            <span className="sch-appraisal-value sch-appraisal-value--gap">${gap.toLocaleString()}</span>
          </div>
        )}
        {gapPercent != null && gapPercent > 0 && (
          <div className="sch-appraisal-row">
            <span className="sch-appraisal-label">Gap % of Offer</span>
            <span className="sch-appraisal-value">{gapPercent.toFixed(1)}%</span>
          </div>
        )}
      </div>

      {recommendedDecision && (
        <div className="sch-card__guide" style={{ borderTop: 'none' }}>
          <div className="sch-card__guide-top">
            <span className="sch-card__guide-title">Decision confidence hint</span>
          </div>
          <p className="sch-helper-text" style={{ marginBottom: 0 }}>
            Based on a {gapPercent?.toFixed(1)}% gap, a <strong>{recommendedDecision === 'reduce_price' ? 'Reduce Price' : 'Split Difference'}</strong> response is typically most likely to keep the deal moving.
          </p>
        </div>
      )}

      <fieldset className="sch-radio-group">
        <legend className="sch-radio-group__legend">Your Decision</legend>
        {[
          { value: 'reduce_price', label: 'Reduce Price' },
          { value: 'split_difference', label: 'Split Difference' },
          { value: 'reject_gap', label: 'Reject Gap' },
        ].map((opt) => (
          <div key={opt.value} className={`sch-decision-option${decision === opt.value ? ' sch-decision-option--selected' : ''}`}>
            <label className="sch-radio-label">
              <input
                type="radio"
                name={`appraisal-decision-${transaction.id}`}
                value={opt.value}
                checked={decision === opt.value}
                onChange={() => setDecision(opt.value)}
                className="sch-radio"
              />
              <span>{opt.label}</span>
              {recommendedDecision === opt.value && <span className="sch-pill">Suggested</span>}
            </label>
            <p className="sch-helper-text" style={{ margin: 0 }}>{APPRAISAL_DECISION_HELP[opt.value]}</p>
          </div>
        ))}
      </fieldset>

      {decision === 'reduce_price' && (
        <div className="sch-price-input">
          <label className="sch-label" htmlFor={`new-price-${transaction.id}`}>New Asking Price ($)</label>
          <input
            id={`new-price-${transaction.id}`}
            type="number"
            min="0"
            step="1"
            className="sch-input"
            placeholder="e.g. 485000"
            value={newPrice}
            onChange={(e) => setNewPrice(e.target.value)}
          />
        </div>
      )}

      {(submitError || error) && (
        <p className="sch-error">{submitError || error}</p>
      )}

      <div className="sch-card__footer">
        <button
          type="button"
          className="sch-submit-btn"
          disabled={loading || !decision || (decision === 'reduce_price' && !(parseFloat(newPrice) > 0))}
          onClick={handleSubmit}
        >
          {loading ? 'Submitting...' : 'Submit Decision'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SellerContingencyHub — main export
// ---------------------------------------------------------------------------
/**
 * @param {{sellerId: (number|string),
 *   focus?: ({transactionId: (number|null), contingencyId: (number|null)}|null)}} props
 *   `focus`: the deal a notification opened the hub on. Its card is focused by
 *   its ContractHub; with no contingency named, the deal's heading is.
 */
export default function SellerContingencyHub({ sellerId, focus = null }) {
  const { transactions, contractTransactions = [], loading, error, reload } = useSellerContingencies(sellerId);
  const [refreshKey, setRefreshKey] = useState(0);
  const contractTxRef = useRef(null);
  const focusedTxRef = useRef(null);
  const focusTxId = focus?.transactionId || null;
  const focusTxLoaded = focusTxId != null && contractTransactions.some((tx) => tx.id === focusTxId);

  useEffect(() => {
    if (!focusTxLoaded || focus?.contingencyId || focusedTxRef.current === focus) return;
    const title = contractTxRef.current?.querySelector(`[data-testid="seller-contract-tx-title-${focusTxId}"]`);
    if (!title) return;
    focusedTxRef.current = focus;
    if (typeof title.scrollIntoView === 'function') title.scrollIntoView({ block: 'start', behavior: 'smooth' });
    title.focus({ preventScroll: true });
  }, [focus, focusTxId, focusTxLoaded]);

  const handleSubmitted = useCallback(() => {
    setRefreshKey((k) => k + 1);
    reload();
  }, [reload]);

  const inspectionTxns = transactions.filter((t) => t.contingency_type === 'inspection');
  const appraisalTxns = transactions.filter((t) => t.contingency_type === 'appraisal');

  // Only the first load replaces the page; a reload after an action keeps it.
  if (loading && transactions.length === 0 && contractTransactions.length === 0) {
    return (
      <div className="sch-root">
        <div className="sch-loading">Loading contingency transactions...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sch-root">
        <div className="sch-error sch-error--page">{error}</div>
        <button type="button" className="sch-retry-btn" onClick={reload}>Retry</button>
      </div>
    );
  }

  return (
    <div className="sch-root">
      <header className="sch-page-header">
        <h2 className="sch-page-title">Contingency Responses</h2>
        <p className="sch-page-subtitle">
          Review each item, make a confident decision, and submit one complete response package per transaction.
        </p>
      </header>

      {/* Wisconsin deals: the contract hub, one card per contingency in the signed offer */}
      {contractTransactions.length > 0 && (
        <section className="sch-section" data-testid="seller-contract-hubs" ref={contractTxRef}>
          <h3 className="sch-section-title">Contingencies in your signed offer</h3>
          {contractTransactions.map((tx) => (
            <div key={tx.id} className="sch-contract-tx" data-testid={`seller-contract-tx-${tx.id}`}>
              <h4
                className="sch-contract-tx__title"
                tabIndex={tx.id === focusTxId ? -1 : undefined}
                data-testid={`seller-contract-tx-title-${tx.id}`}
              >
                {contractTransactionTitle(tx)}
              </h4>
              <ContractHub
                key={tx.id}
                transaction={tx}
                role="seller"
                onRefresh={reload}
                focusContingencyId={!focusTxId || tx.id === focusTxId ? focus?.contingencyId : null}
              />
            </div>
          ))}
        </section>
      )}

      {/* Inspection Section */}
      <section className="sch-section">
        <h3 className="sch-section-title">Inspection Responses</h3>
        {inspectionTxns.length === 0 ? (
          <p className="sch-empty sch-empty--section">No pending inspection responses.</p>
        ) : (
          <div className="sch-card-list" key={`insp-${refreshKey}`}>
            {inspectionTxns.map((txn) => (
              <InspectionTransactionCard key={txn.id} transaction={txn} onSubmitted={handleSubmitted} />
            ))}
          </div>
        )}
      </section>

      {/* Appraisal Section */}
      <section className="sch-section">
        <h3 className="sch-section-title">Appraisal Gap Decisions</h3>
        {appraisalTxns.length === 0 ? (
          <p className="sch-empty sch-empty--section">No pending appraisal gap decisions.</p>
        ) : (
          <div className="sch-card-list" key={`appr-${refreshKey}`}>
            {appraisalTxns.map((txn) => (
              <AppraisalTransactionCard key={txn.id} transaction={txn} onSubmitted={handleSubmitted} />
            ))}
          </div>
        )}
      </section>

      {transactions.length === 0 && contractTransactions.length === 0 && (
        <div className="sch-empty sch-empty--page">
          No active contingency transactions at this time.
        </div>
      )}
    </div>
  );
}
